import { makeStyles } from '@material-ui/core/styles';
import { redesignTheme } from '../../theme';

const { colors, fonts, fontSize, spacing, radius, breakpoints, sizes } = redesignTheme;

export const useFooterStyles = makeStyles(() => ({
  root: {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: colors.footerSurface,
    borderTop: `1px solid ${colors.border}`,
    padding: `${spacing.md}px ${spacing.sm*4}px ${spacing.sm}px`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing.xl,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      gap: spacing.md,
      padding: `${spacing.md}px ${spacing.sm}px ${spacing.sm}px`,
    },
  },

  inner: {
    width: '100%',
    margin: '0 auto',
  },

  topSection: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 168,
    [`@media (max-width: ${breakpoints.desktop}px)`]: {
      gap: spacing.xl,
    },
    [`@media (max-width: ${breakpoints.tablet}px)`]: {
      flexDirection: 'column',
      gap: spacing.lg,
    },
  },

  contactColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 35,
    width: 233,
    flexShrink: 0,
    [`@media (max-width: ${breakpoints.tablet}px)`]: {
      width: '100%',
    },
  },

  contactText: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    color: colors.textBody,
    lineHeight: 'normal',
    margin: 0,
    '& p': {
      margin: 0,
    },
  },

  contactPhone: {
    margin: 0,
    lineHeight: 'normal',
    '& p': {
      margin: 0,
    },
  },

  contactButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    width: '100%',
    height: sizes.buttonHeight,
    padding: `${spacing.sm}px ${spacing.md}px`,
    boxSizing: 'border-box',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    backgroundColor: colors.white,
    color: colors.primaryDark,
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.md,
    letterSpacing: '0.28px',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: colors.hoverSurface,
    },
  },

  contactButtonIcon: {
    fontSize: 16,
    color: colors.primaryDark,
  },

  servicesColumn: {
    flex: 1,
    minWidth: 0,
  },

  servicesTitle: {
    margin: `0 0 ${spacing.sm}px`,
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.md,
    color: colors.textBody,
    lineHeight: 'normal',
  },

  servicesGrid: {
    display: 'flex',
    gap: spacing.md,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      flexDirection: 'column',
      gap: spacing.sm,
    },
  },

  servicesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flex: 1,
    minWidth: 0,
  },

  serviceLink: {
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    color: colors.textBody,
    lineHeight: 'normal',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  divider: {
    width: '100%',
    height: 1,
    backgroundColor: colors.border,
    border: 0,
    margin: 0,
  },

  certificationsSection: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.md,
    [`@media (max-width: ${breakpoints.tablet}px)`]: {
      flexDirection: 'column',
      gap: spacing.lg,
    },
  },

  certificationsLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
    flex: 1,
    minWidth: 0,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },

  logosRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
    flexShrink: 0,
  },

  qualiopiLogo: {
    width: 155,
    height: 'auto',
    objectFit: 'contain',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      width: 120,
    },
  },

  europeLogo: {
    width: 112,
    height: 'auto',
    objectFit: 'contain',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      width: 88,
    },
  },

  certificationText: {
    margin: 0,
    fontFamily: fonts.opensans,
    fontSize: fontSize.xs,
    color: colors.textBody,
    lineHeight: 'normal',
    maxWidth: 491,
    whiteSpace: 'pre-wrap',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      maxWidth: '100%',
    },
  },

  brandColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: spacing.md,
    width: 268,
    flexShrink: 0,
    [`@media (max-width: ${breakpoints.tablet}px)`]: {
      alignItems: 'flex-start',
      width: '100%',
    },
  },

  brandLogo: {
    width: 209,
    height: 'auto',
    objectFit: 'contain',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      width: 180,
    },
  },

  brandTagline: {
    margin: 0,
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    color: colors.textBody,
    lineHeight: 'normal',
    textAlign: 'right',
    [`@media (max-width: ${breakpoints.tablet}px)`]: {
      textAlign: 'left',
    },
  },

  bottomBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    width: '100%',
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    color: colors.textBody,
    lineHeight: 'normal',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: spacing.sm,
    },
  },

  copyright: {
    margin: 0,
    whiteSpace: 'nowrap',
  },

  legalLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
  },

  legalLink: {
    border: 0,
    padding: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    color: colors.textBody,
    lineHeight: 'normal',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  legalSeparator: {
    color: colors.textBody,
    userSelect: 'none',
  },
}));
